/**
 * GSAP Initialization
 *
 * Sets up GSAP and ScrollTrigger with optimal defaults.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize GSAP with default settings
 */
export function initGSAP(): void {
  // Set default easing
  gsap.defaults({
    ease: 'power2.out',
    duration: 0.6,
  });

  // Configure ScrollTrigger defaults
  ScrollTrigger.config({
    autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load',
  });

  // Respect reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    gsap.config({
      autoSleep: 60,
      force3D: false,
    });
  }
}

/**
 * Create a ScrollTrigger animation
 */
export function createScrollAnimation(
  element: string | Element,
  animation: gsap.TweenVars,
  scrollOptions?: ScrollTrigger.Vars
): ScrollTrigger | null {
  if (typeof gsap === 'undefined' || !ScrollTrigger) {
    return null;
  }

  return ScrollTrigger.create({
    trigger: element,
    start: 'top 80%',
    end: 'bottom 20%',
    toggleActions: 'play none none reverse',
    ...scrollOptions,
    animation: gsap.to(element, animation),
  });
}

/**
 * Animate element on scroll into view
 */
export function animateOnScroll(
  element: string | Element,
  animation: gsap.TweenVars,
  options?: ScrollTrigger.Vars
): ScrollTrigger | null {
  return createScrollAnimation(element, animation, {
    start: 'top 80%',
    toggleActions: 'play none none reverse',
    ...options,
  });
}

export { gsap, ScrollTrigger };
